<script lang="ts" setup>
  import { useHistoryStore } from "~/stores/HistoryStore";
  import { ArticleInfo } from "~/entities/ArticleInfo";
  import { getPublishStatusLabel } from "~/consts/contentsConfig";

  interface Props {
    content: ArticleInfo;
  }
  defineProps<Props>();

  const isTablet = isTabletAndSpDisplayRef();
  const historyStore = useHistoryStore();

  const onClick = (content: ArticleInfo) => {
    historyStore.to(`/plan/${content.plan?.id}/article/${content.id}`);
  };
  const onClickSetting = (content: ArticleInfo) => {
    historyStore.to(`/plan/${content.plan?.id}/distribute/article/${content.id}/edit`);
  };

  // 予約状態かどうか
  const isReserved = (content: ArticleInfo) => {
    // publishDateTimeがnull, undefined, 空文字, 過去の場合は予約状態ではない
    if (!content.publishDateTime) {
      return false;
    }
    if (new Date(content.publishDateTime) < new Date()) {
      return false;
    }
    return true;
  };

  // ドラフトかどうか
  const isDraft = (content: ArticleInfo) => {
    return content.articleStatus === "DRAFT";
  };

  const publishDateText = (content: ArticleInfo) => {
    if (isReserved(content) || isDraft(content)) {
      return `${GetElapsedText(content.postDateTime)}に作成`;
    }
    if (content.publishDateTime) {
      return `${GetElapsedText(content.publishDateTime)}に公開`;
    }
    return `${GetElapsedText(content.postDateTime)}に公開`;
  };
</script>
<template>
  <div class="list-item" @click="onClick(content)">
    <!-- thumbnail -->
    <div class="list-item-thumbnail d-flex align-center">
      <AtomsImgWithLoading
        :width="isTablet ? 100 : 140"
        :height="isTablet ? 58 : 78"
        :src="content.thumbnailImage"
        contains
        class="rounded"
      />
    </div>
    <!-- contents -->
    <div class="list-item-content">
      <!-- free or member -->
      <div class="mx-0 my-1 text-free d-flex justify-start align-end">
        <v-icon v-if="!content.isFree" class="mr-1" :size="20" icon="$mdiLock" />
        <span>{{ content.isFree ? "無料公開" : "会員限定" }}</span>
      </div>

      <!-- title -->
      <div class="list-item-title my-1">
        <span class="text-truncate text">
          {{ content.contentsTitle }}
        </span>
      </div>
      <!-- いいね、投稿時間 -->
      <div class="d-flex align-center justify-start my-1">
        <div v-if="content.postDateTime || content.publishDateTime" class="ma-0" :height="22">
          <p class="ma-0 text-publish-date text-right text-monochrome-02">
            {{ publishDateText(content) }}
          </p>
        </div>
        <OrganismsContentsGoodButton
          class="good-count-button"
          :content-id="content.id"
          :is-gooded="content.isGood ?? false"
          :count="content.goodCount"
          :clickable="true"
          color="monochrome-02"
        />
        <OrganismsContentsBookmarkButton
          :content-id="content.id"
          :is-bookmark="!!content.isBookmark"
          :clickable="true"
          :hide-bg-color="true"
        />
      </div>
    </div>
    <!-- publish status or draft status -->
    <div
      v-if="isDraft(content) || content.publishStatus !== 'PUBLISH' || isReserved(content)"
      class="status-area d-flex align-center justify-center flex-column"
    >
      <div v-if="content.publishStatus !== 'PUBLISH'" data-testid="article-list-status">
        {{ getPublishStatusLabel(content.publishStatus) }}
      </div>
      <div v-if="isDraft(content)" data-testid="article-list-draft">下書き</div>
      <div v-else-if="isReserved(content)" data-testid="article-list-reserve">公開予約</div>
    </div>
    <!-- unread badge -->
    <v-badge
      v-if="content.isUnread"
      color="red"
      dot
      class="ml-auto pr-1"
      :offset-x="8"
      :offset-y="12"
      data-testid="article-list-item-badge"
    />
    <!-- setting icon -->
    <v-icon
      v-if="content.isOwner"
      icon="$mdiCogOutline"
      class="setting-button text-monochrome-02"
      :size="22"
      data-testid="article-list-setting-button"
      @click.stop="onClickSetting(content)"
    />
  </div>
</template>
<style lang="scss" scoped>
  @import "assets/media.scss";
  .list-item {
    display: flex;
    margin: 0px 20px 10px 20px;
    cursor: pointer;
    position: relative;
    background: rgb(var(--v-theme-monochrome-04));
    border-radius: 4px;
    @include display_tablet {
      margin: 0px 10px 10px 10px;
    }
  }
  .status-area {
    position: absolute;
    height: 100%;
    width: 100%;
    top: 0;
    right: 0;
    background-color: rgb(var(--v-theme-monochrome-08), 0.8);
    div {
      font-size: 14px;
      color: white;
      padding: 2px 20px 4px 20px;
      margin: 4px 4px;
      border-radius: 30px;
      background-color: rgb(var(--v-theme-monochrome-04));
    }
  }

  .list-item-thumbnail {
    width: 140px;
    border-radius: 4px;
    overflow: hidden;
  }
  .list-item-content {
    padding: 5px 10px 0 10px;
    flex-grow: 1;
    overflow: hidden;
  }
  .list-item-title {
    display: flex;
    align-items: center;
    gap: 4px;
    height: 22px;
    overflow: hidden;
    .icon {
      width: 22px;
      height: 22px;
    }
    .text {
      flex-grow: 1;
      height: 22px;
      font-size: 16px;
      font-weight: 700;
      width: calc(100vw - 248px - 400px);
      max-width: calc(1280px - 248px - 400px);
    }
  }

  .text-free {
    font-size: 14px;
    font-weight: 400;
    line-height: 20px; // レイアウトが特殊なので個別指定
    letter-spacing: 0.06em;
  }

  .text-publish-date {
    font-size: 14px;
    font-weight: 400;
    line-height: 24px; // レイアウトが特殊なので個別指定
    letter-spacing: 0.06em;
  }

  .good-count-button {
    height: 24px;
    padding-top: 4px;
    background-color: transparent;
  }

  .setting-button {
    position: absolute;
    top: 0;
    bottom: 0;
    right: 8px;
    margin: auto 0;
    cursor: pointer;
  }
</style>
